import { PaymentProvider } from "@/api-v3";

/** What the disclaimer rules need to know about a card. */
export interface CardDisclaimerFacts {
  provider: PaymentProvider;
  /**
   * PPS `source_platform` of the payment method the card was issued from, e.g.
   * "GB", "EU" or "US". Cards carry no platform of their own.
   */
  sourcePlatform?: string;
}

interface CardDisclaimerRule {
  appliesTo: (facts: CardDisclaimerFacts) => boolean;
  translationKey: string;
}

type StripePlatform = "GB" | "EU" | "US";

const isHighnoteCard = ({ provider }: CardDisclaimerFacts) =>
  provider === PaymentProvider.highnote;

const isStripeCardIssuedOn =
  (platform: StripePlatform) =>
  ({ provider, sourcePlatform }: CardDisclaimerFacts) =>
    provider === PaymentProvider.stripe && sourcePlatform === platform;

/**
 * Each issuer is a separately regulated entity with its own disclosure: Sutton
 * Bank for Highnote, and one Stripe entity per platform. Checked in order, the
 * first rule that applies wins.
 */
const cardDisclaimerRules: CardDisclaimerRule[] = [
  { appliesTo: isHighnoteCard, translationKey: "sutton_disclaimer" },
  {
    appliesTo: isStripeCardIssuedOn("GB"),
    translationKey: "card_stripe_disclaimer_uk",
  },
  {
    appliesTo: isStripeCardIssuedOn("EU"),
    translationKey: "card_stripe_disclaimer_eu",
  },
  {
    appliesTo: isStripeCardIssuedOn("US"),
    translationKey: "card_stripe_disclaimer_us",
  },
];

/**
 * i18n key of the disclosure for a card, or `null` when no rule applies, e.g. a
 * Stripe card whose platform is unknown. Showing no disclosure beats showing
 * another regulator's.
 */
export const getCardDisclaimerKey = (
  facts: CardDisclaimerFacts
): string | null =>
  cardDisclaimerRules.find((rule) => rule.appliesTo(facts))?.translationKey ??
  null;
